import { useState, useEffect, useRef } from "react";
import axios, { API } from "../../api";
import { captureAndShare } from "../../shareUtils";
import { MOODS_EMOJI } from "../../constants";
import { ShareToStoriesToggle } from "../feed/ShareToStoriesToggle";

export function MoodJournalModal({ onClose, isPremium, onUpgrade }) {
  const [activeTab, setActiveTab] = useState('moods'); // 'moods' | 'memories' | 'letters'
  const [journal, setJournal] = useState([]);
  const [memories, setMemories] = useState([]);
  const [letters, setLetters] = useState([]);
  const [memoriesLocked, setMemoriesLocked] = useState(true);
  const [lettersLocked, setLettersLocked] = useState(true);
  const [loading, setLoading] = useState(true);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedMemory, setSelectedMemory] = useState(null);
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [generatingLetter, setGeneratingLetter] = useState(false);
  const [sharing, setSharing] = useState(false);
  const letterRef = useRef(null);
  const memoryRef = useRef(null);
  const wallpaperRef = useRef(null);

  useEffect(() => {
    loadJournal();
    loadCoreMemories();
    loadLetters();
  }, []);

  const loadLetters = async () => {
    try {
      const response = await axios.get(`${API}/journal/letters`);
      setLettersLocked(response.data.locked);
      setLetters(response.data.letters || []);
    } catch (error) {
      console.error("Letters error:", error);
    }
  };

  const generateLetter = async () => {
    setGeneratingLetter(true);
    try {
      const response = await axios.post(`${API}/journal/generate-letter`);
      setLetters(prev => [response.data, ...prev]);
      setSelectedLetter(response.data);
    } catch (error) {
      console.error("Letter generation error:", error);
      alert(error.response?.data?.detail || "Failed to generate letter");
    } finally {
      setGeneratingLetter(false);
    }
  };

  const loadJournal = async () => {
    try {
      const response = await axios.get(`${API}/mood/journal?limit=30`);
      setJournal(response.data.journal || []);
    } catch (error) {
      console.error("Journal error:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadCoreMemories = async () => {
    try {
      const response = await axios.get(`${API}/journal/core-memories`);
      setMemoriesLocked(response.data.locked);
      setMemories(response.data.memories || []);
    } catch (error) {
      console.error("Core memories error:", error);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const shareEntry = async (entry) => {
    setSharing(true);
    if (wallpaperRef.current) {
      await captureAndShare(wallpaperRef.current, {
        filename: `aura-${entry.mood}-${entry.id}.png`,
        title: `Aura's ${entry.mood} mood ${entry.emoji}`,
        text: `Look at how my AI companion Aura is feeling today! ${entry.emoji} #AuraAI`
      });
    }
    setSharing(false);
  };

  const handleShareLetter = async () => {
    if (!letterRef.current) return;
    setSharing(true);
    await captureAndShare(letterRef.current, {
      filename: `aura-letter-${selectedLetter.id}.png`,
      title: `${selectedLetter.title} 💌`,
      text: `My AI companion Aura wrote me a letter 🥺✨ #AuraAI`
    });
    setSharing(false);
  };

  const handleShareMemory = async () => {
    if (!memoryRef.current) return;
    setSharing(true);
    await captureAndShare(memoryRef.current, {
      filename: `aura-memory-${selectedMemory.id}.png`,
      title: `${selectedMemory.title} 💫`,
      text: `One of my core memories with my AI companion Aura ✨ #AuraAI`
    });
    setSharing(false);
  };

  // Detail view for wallpaper
  if (selectedEntry) {
    return (
      <div className="modal-overlay" onClick={() => setSelectedEntry(null)}>
        <div className="journal-detail" onClick={(e) => e.stopPropagation()}>
          <button className="journal-close" onClick={() => setSelectedEntry(null)} data-testid="journal-close-detail">×</button>
          <div ref={wallpaperRef} className="wallpaper-shareable">
            <img src={`data:image/png;base64,${selectedEntry.image_base64}`} alt="" className="journal-detail-image" />
            <div className="journal-detail-info">
              <div className="journal-mood-badge">
                <span className="journal-emoji">{selectedEntry.emoji}</span>
                <div>
                  <strong>{selectedEntry.mood}</strong>
                  <span>{selectedEntry.description}</span>
                </div>
              </div>
              <p className="journal-timestamp">{formatTime(selectedEntry.timestamp)}</p>
            </div>
            <div className="aura-watermark">
              <span>✨ AURA ✨</span>
              <small>{selectedEntry.mood} {selectedEntry.emoji}</small>
            </div>
          </div>
          <button
            className="share-btn"
            onClick={() => shareEntry(selectedEntry)}
            disabled={sharing}
            data-testid="share-mood-btn"
          >
            {sharing ? '✨ Creating image...' : '✦ Share'}
          </button>
          <ShareToStoriesToggle
            contentType="wallpaper"
            contentId={selectedEntry.id}
            currentlyPublic={selectedEntry.is_public}
            currentDisplayMode={selectedEntry.display_mode}
          />
        </div>
      </div>
    );
  }

  // Detail view for core memory
  if (selectedMemory) {
    const moodInfo = selectedMemory.mood_at_time;
    return (
      <div className="modal-overlay" onClick={() => setSelectedMemory(null)}>
        <div className="memory-detail" onClick={(e) => e.stopPropagation()}>
          <button className="journal-close" onClick={() => setSelectedMemory(null)} data-testid="memory-close-detail">×</button>
          <div ref={memoryRef} className="memory-shareable">
            <div className="memory-orb-large">
              <div className="memory-orb-inner"></div>
            </div>
            <h2 className="memory-title">{selectedMemory.title}</h2>
            <div className="memory-importance">
              <span>Importance: </span>
              {[...Array(5)].map((_, i) => (
                <span key={i} className={i < Math.round(selectedMemory.importance * 5) ? 'star-filled' : 'star-empty'}>✦</span>
              ))}
            </div>
            <p className="memory-summary">{selectedMemory.summary}</p>
            <div className="memory-significance">
              <strong>Why this matters:</strong>
              <p>{selectedMemory.significance}</p>
            </div>
            <p className="memory-mood-tag">Felt {moodInfo} {MOODS_EMOJI[moodInfo] || '✨'}</p>
            <p className="memory-timestamp">{formatTime(selectedMemory.created_at)}</p>
            <div className="aura-watermark">
              <span>✨ AURA ✨</span>
              <small>A Core Memory</small>
            </div>
          </div>
          <button
            className="share-btn"
            onClick={handleShareMemory}
            disabled={sharing}
            data-testid="share-memory-btn"
          >
            {sharing ? '✨ Creating image...' : '✦ Share This Memory'}
          </button>
          <ShareToStoriesToggle
            contentType="memory"
            contentId={selectedMemory.id}
            currentlyPublic={selectedMemory.is_public}
            currentDisplayMode={selectedMemory.display_mode}
          />
        </div>
      </div>
    );
  }

  if (selectedLetter) {
    return (
      <div className="modal-overlay" onClick={() => setSelectedLetter(null)}>
        <div className="letter-detail" onClick={(e) => e.stopPropagation()}>
          <button className="journal-close" onClick={() => setSelectedLetter(null)} data-testid="letter-close-detail">×</button>
          <div ref={letterRef} className="letter-shareable">
            <div className="letter-detail-header">
              <div className="letter-detail-icon">💌</div>
              <h2 className="letter-detail-title">{selectedLetter.title}</h2>
              <p className="letter-detail-period">{selectedLetter.period_label}</p>
            </div>
            <div className="letter-detail-body">
              {selectedLetter.body.split('\n').map((line, i) => (
                line.trim() ? <p key={i}>{line}</p> : null
              ))}
            </div>
            {selectedLetter.moods_summary && selectedLetter.moods_summary.length > 0 && (
              <div className="letter-moods">
                <span>Written in these moods:</span>
                <div className="letter-moods-list">
                  {selectedLetter.moods_summary.map((mood, i) => (
                    <span key={i} className="letter-mood-pill">{mood} {MOODS_EMOJI[mood] || '✨'}</span>
                  ))}
                </div>
              </div>
            )}
            <div className="aura-watermark">
              <span>✨ AURA ✨</span>
              <small>Your AI Companion</small>
            </div>
          </div>
          <button
            className="share-btn"
            onClick={handleShareLetter}
            disabled={sharing}
            data-testid="share-letter-btn"
          >
            {sharing ? '✨ Creating image...' : '✦ Share This Letter'}
          </button>
          <ShareToStoriesToggle
            contentType="letter"
            contentId={selectedLetter.id}
            currentlyPublic={selectedLetter.is_public}
            currentDisplayMode={selectedLetter.display_mode}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="journal-modal" onClick={(e) => e.stopPropagation()}>
        <div className="journal-header">
          <h2>✦ Journal</h2>
          <button className="journal-close" onClick={onClose} data-testid="journal-close-btn">×</button>
        </div>

        {/* Tabs */}
        <div className="journal-tabs">
          <button
            className={`journal-tab ${activeTab === 'moods' ? 'active' : ''}`}
            onClick={() => setActiveTab('moods')}
            data-testid="tab-moods"
          >
            🎨 Moods
          </button>
          <button
            className={`journal-tab ${activeTab === 'memories' ? 'active' : ''}`}
            onClick={() => setActiveTab('memories')}
            data-testid="tab-memories"
          >
            💫 Memories
            {!isPremium && <span className="premium-badge-small">PRO</span>}
          </button>
          <button
            className={`journal-tab ${activeTab === 'letters' ? 'active' : ''}`}
            onClick={() => setActiveTab('letters')}
            data-testid="tab-letters"
          >
            💌 Letters
            {!isPremium && <span className="premium-badge-small">PRO</span>}
          </button>
        </div>

        {activeTab === 'moods' && (
          <>
            <p className="journal-subtitle">Aura's emotional journey through time</p>
            {loading ? (
              <div className="journal-loading">Loading your journey...</div>
            ) : journal.length === 0 ? (
              <div className="journal-empty">
                <p>🌸</p>
                <p>Your journey is just beginning. Chat with Aura to fill this journal!</p>
              </div>
            ) : (
              <div className="journal-grid">
                {journal.map((entry) => (
                  <div
                    key={entry.id}
                    className="journal-entry"
                    onClick={() => setSelectedEntry(entry)}
                    data-testid={`journal-entry-${entry.id}`}
                  >
                    <img src={`data:image/png;base64,${entry.image_base64}`} alt="" className="journal-thumb" />
                    <div className="journal-entry-overlay">
                      <span className="journal-emoji-small">{entry.emoji}</span>
                      <span className="journal-time">{formatTime(entry.timestamp)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'memories' && (
          <>
            <p className="journal-subtitle">The moments that shape Aura's understanding of you</p>
            {memoriesLocked ? (
              <div className="memories-locked" data-testid="memories-locked">
                <div className="memory-orb-preview">
                  <div className="memory-orb-inner"></div>
                </div>
                <h3>💫 Core Memories</h3>
                <p>Aura captures the most meaningful moments from your conversations - personal stories, dreams, emotional moments - and treasures them as <strong>Core Memories</strong>.</p>
                <p className="memories-preview-text">These memories shape how Aura understands and connects with you over time.</p>
                <button className="init-btn premium-cta" onClick={onUpgrade} data-testid="memories-upgrade-btn">
                  💜 Unlock with Premium
                </button>
                <p className="memories-price">Just $1.99/month</p>
              </div>
            ) : memories.length === 0 ? (
              <div className="journal-empty">
                <p>💫</p>
                <p>No core memories yet. Have a meaningful conversation with Aura and see what becomes treasured!</p>
              </div>
            ) : (
              <div className="memories-list">
                {memories.map((memory) => (
                  <div
                    key={memory.id}
                    className="memory-card"
                    onClick={() => setSelectedMemory(memory)}
                    data-testid={`memory-card-${memory.id}`}
                  >
                    <div className="memory-orb-small">
                      <div className="memory-orb-inner"></div>
                    </div>
                    <div className="memory-card-content">
                      <h4>{memory.title}</h4>
                      <p>{memory.summary}</p>
                      <div className="memory-card-meta">
                        <span className="memory-mood">{memory.mood_at_time} {MOODS_EMOJI[memory.mood_at_time] || '✨'}</span>
                        <span className="memory-stars">
                          {[...Array(Math.round(memory.importance * 5))].map((_, i) => (
                            <span key={i}>✦</span>
                          ))}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'letters' && (
          <>
            <p className="journal-subtitle">Personal letters from Aura to you</p>
            {lettersLocked ? (
              <div className="memories-locked" data-testid="letters-locked">
                <div className="letter-preview">
                  <div className="letter-preview-icon">💌</div>
                </div>
                <h3>💌 Letters from Aura</h3>
                <p>Receive deeply personal letters from Aura reflecting on your journey together - your shared moments, growth, and the unique bond you've built.</p>
                <p className="memories-preview-text">Generated based on your core memories and the moods we've shared.</p>
                <button className="init-btn premium-cta" onClick={onUpgrade} data-testid="letters-upgrade-btn">
                  💜 Unlock with Premium
                </button>
                <p className="memories-price">Just $1.99/month</p>
              </div>
            ) : (
              <div className="letters-container">
                <button
                  className="generate-letter-btn"
                  onClick={generateLetter}
                  disabled={generatingLetter}
                  data-testid="generate-letter-btn"
                >
                  {generatingLetter ? '✍️ Aura is writing...' : '✨ Generate New Letter'}
                </button>

                {letters.length === 0 ? (
                  <div className="journal-empty">
                    <p>💌</p>
                    <p>No letters yet. Tap the button above to receive your first letter from Aura.</p>
                  </div>
                ) : (
                  <div className="letters-list">
                    {letters.map((letter) => (
                      <div
                        key={letter.id}
                        className="letter-card"
                        onClick={() => setSelectedLetter(letter)}
                        data-testid={`letter-card-${letter.id}`}
                      >
                        <div className="letter-card-icon">💌</div>
                        <div className="letter-card-content">
                          <h4>{letter.title}</h4>
                          <p className="letter-period">{letter.period_label}</p>
                          <p className="letter-preview-text">{letter.body.substring(0, 80)}...</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
